import apiService from './api.service';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface UsageConfig {
  infraHosts?: number;
  kubernetesNodes?: number;
  apmTransactions?: number;
  logsVolume?: number;
  customMetrics?: number;
}

export interface RegisterRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  tenantName: string;
  subdomain: string;
  planSlug?: string;
  usageConfig?: UsageConfig;
  requirePaymentMethod?: boolean;
}

export interface AuthResponse {
  success: boolean;
  data: {
    user: {
      id: string;
      email: string;
      firstName: string;
      lastName: string;
      tenantId: string;
      roles: string[];
      isSuperAdmin?: boolean;
    };
    token: string;
    refreshToken: string;
    requiresPaymentSetup?: boolean;
    tenantId?: string;
    planSlug?: string;
  };
}

export interface SetupPaymentRequest {
  tenantId: string;
  email: string;
  planSlug: string;
  billingCycle?: 'monthly' | 'yearly';
  currency?: 'USD' | 'BRL';
}

export interface SetupPaymentResponse {
  success: boolean;
  data: {
    checkoutUrl: string;
    sessionId: string;
  };
}

export interface CompletePaymentSetupRequest {
  sessionId: string;
  tenantId: string;
}

export interface CompletePaymentSetupResponse {
  success: boolean;
  message: string;
}

export interface RefreshTokenResponse {
  success: boolean;
  data: {
    token: string;
    refreshToken: string;
  };
}

export interface MeResponse {
  success: boolean;
  data: {
    user: {
      id: string;
      email: string;
      firstName: string;
      lastName: string;
      tenantId: string;
      roles: string[];
      isSuperAdmin?: boolean;
    };
  };
}

export interface ForgotPasswordResponse {
  success: boolean;
  message: string;
}

export interface ResetPasswordResponse {
  success: boolean;
  message: string;
}

class AuthApi {
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    return apiService.post<AuthResponse>('/v1/auth/login', credentials);
  }

  async register(data: RegisterRequest): Promise<AuthResponse> {
    return apiService.post<AuthResponse>('/v1/auth/register', data);
  }

  async refreshToken(refreshToken: string): Promise<RefreshTokenResponse> {
    return apiService.post<RefreshTokenResponse>('/v1/auth/refresh', { refreshToken });
  }

  async getMe(): Promise<MeResponse> {
    return apiService.get<MeResponse>('/v1/auth/me');
  }

  async logout(): Promise<void> {
    return apiService.post('/v1/auth/logout');
  }

  async setupPayment(data: SetupPaymentRequest): Promise<SetupPaymentResponse> {
    return apiService.post<SetupPaymentResponse>('/v1/auth/setup-payment', data);
  }

  async completePaymentSetup(data: CompletePaymentSetupRequest): Promise<CompletePaymentSetupResponse> {
    return apiService.post<CompletePaymentSetupResponse>('/v1/auth/complete-payment-setup', data);
  }

  async forgotPassword(email: string): Promise<ForgotPasswordResponse> {
    return apiService.post<ForgotPasswordResponse>('/v1/auth/forgot-password', { email });
  }

  async resetPassword(token: string, newPassword: string): Promise<ResetPasswordResponse> {
    return apiService.post<ResetPasswordResponse>('/v1/auth/reset-password', { token, newPassword });
  }
}

export default new AuthApi();
